import type { Country } from '@atlasguess/engine';
import type { CountryFilterCriteria } from '../config';
import { getCountrySet } from './curatedSets';

/**
 * Apply a CountryFilterCriteria to a country list. Mirrors the engine's
 * `filterCities` shape — kept on the games side because the criteria
 * schema lives here (with the rest of game-mode config).
 *
 * Composition rules:
 *   - `additionalIsos` short-circuits to TRUE — those countries pass
 *     regardless of the rest of the filter. Used for region quizzes
 *     that want the UN-members baseline plus a deliberate exception
 *     (e.g. Africa quiz including Western Sahara).
 *   - All other criteria AND-combine (a country must pass every active
 *     filter).
 *   - `setName` resolves via `framework/curatedSets.ts` — `un-members`,
 *     etc. Unknown set names are ignored (logged once would be nice; for
 *     now, silent so a typo doesn't crash a quiz mid-run).
 *   - `excludeHidden` defaults true so the filter respects the dataset's
 *     own opt-outs without callers having to remember.
 *   - `excludeIsos` always wins — even if a country is in `additionalIsos`,
 *     `excludeIsos` overrides (lets us defensively block sensitive ISOs
 *     without worrying about an `additionalIsos` override re-introducing
 *     them).
 */
export function filterCountries(
  countries: readonly Country[],
  criteria: CountryFilterCriteria,
): Country[] {
  const excludeHidden = criteria.excludeHidden ?? true;
  const continents = criteria.continents ? new Set(criteria.continents) : null;
  const isoAllow = criteria.isos ? new Set(criteria.isos) : null;
  const isoExclude = criteria.excludeIsos ? new Set(criteria.excludeIsos) : null;
  const additional = criteria.additionalIsos ? new Set(criteria.additionalIsos) : null;
  const curatedSet = criteria.setName ? getCountrySet(criteria.setName) : null;

  return countries.filter((c) => {
    // excludeIsos wins over everything, including additionalIsos.
    if (isoExclude && isoExclude.has(c.iso_a2)) return false;
    // additionalIsos shortcuts the rest of the filter.
    if (additional && additional.has(c.iso_a2)) return true;
    if (excludeHidden && c.hidden) return false;
    if (continents && !continents.has(c.continent)) return false;
    if (isoAllow && !isoAllow.has(c.iso_a2)) return false;
    if (curatedSet && !curatedSet.has(c.iso_a2)) return false;
    return true;
  });
}
